// ============================================================================
// WorkoutCard — a created/updated workout
// Sport-colored 1px border + icon chip, title, mono meta row, up to 3 block
// rows with zone dots and a "+N more" row.
// ============================================================================

import { Box, Typography } from '@mui/material'
import { typography, spacing } from '@/theme/muiTheme'
import { getSportIcon } from '@/lib/sportIcons'
import { activityTypeToSport } from '@/types/workouts'
import type { RenderPayload, WorkoutCardSpec, WorkoutBlockSpec } from '@/types/chatRenders'
import CardShell, { monoMetaSx } from './CardShell'
import { sportDotColor, zoneDotColor, formatWeekdayDate } from './cardUtils'

const MAX_BLOCK_ROWS = 3

// Sport enum -> the snake_case icon keys getSportIcon resolves.
const SPORT_ICON_KEYS: Record<string, string> = {
  HYBRID: 'fitness_center',
  AEROBIC: 'favorite',
  RUNNING: 'directions_run',
  BIKING: 'directions_bike',
  SKATING: 'ice_skating',
  SWIMMING: 'pool',
  STRENGTH: 'fitness_center',
  PADDLING: 'kayaking',
  OTHER: 'sports_score',
}

function BlockRow({ block }: { block: WorkoutBlockSpec }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minWidth: 0,
        px: spacing.md,
        py: '8px',
        borderTop: '1px solid rgba(248, 246, 244, 0.09)',
      }}
    >
      {/* Square zone dot (radius 2), per the frames' zone palette rows. */}
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '2px',
          backgroundColor: zoneDotColor(block.zone),
          flexShrink: 0,
        }}
      />
      <Typography noWrap sx={{ fontSize: '13px', flex: 1, minWidth: 0 }}>
        {block.name}
      </Typography>
      {/* Value column: right-aligned, system font, tabular numerals. */}
      <Typography
        noWrap
        sx={{
          fontSize: '13px',
          color: 'text.secondary',
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0,
        }}
      >
        {block.summary}
      </Typography>
    </Box>
  )
}

export default function WorkoutCard({ render }: { render: RenderPayload }) {
  const spec = render.spec as unknown as Partial<WorkoutCardSpec>
  const sport = spec.sport ?? ''
  const color = sportDotColor(sport)
  const SportIcon = getSportIcon(SPORT_ICON_KEYS[activityTypeToSport(sport)])
  const blocks = Array.isArray(spec.blocks) ? spec.blocks : []
  const visibleBlocks = blocks.slice(0, MAX_BLOCK_ROWS)
  const hiddenCount = (spec.more_blocks ?? 0) + Math.max(0, blocks.length - MAX_BLOCK_ROWS)
  const scheduled = formatWeekdayDate(spec.scheduled_date ?? render.entity.date)
  const totalBlocks = blocks.length + (spec.more_blocks ?? 0)

  // Kind row carries sport + date per the frames (WORKOUT · STRENGTH · FRI 17 JUL).
  const kindLabel = ['Workout', spec.sport || null,
                     scheduled || (render.status === 'updated' ? 'Updated' : null)]
    .filter(Boolean).join(' · ')

  // Meta: estimate + exercise count. The date lives in the kind row, not here.
  const metaParts = [
    spec.est_minutes != null ? `~${spec.est_minutes} min` : null,
    totalBlocks ? `${totalBlocks} exercises` : null,
  ].filter(Boolean)

  return (
    <CardShell
      kindLabel={kindLabel}
      kindColor={color}
      borderColor={`${color}73`} /* sport color at ~45% — border encodes category */
      action={{ label: scheduled ? 'View on calendar →' : 'Open →', to: '/calendar' }}
      disableBodyPadding
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.sm, px: spacing.md, py: spacing.sm }}>
        {/* Sport icon chip — accent repeated on the chip border per the frames. */}
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '6px',
            backgroundColor: `${color}29`,
            border: `1px solid ${color}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <SportIcon sx={{ fontSize: 13, color }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '15px', fontWeight: typography.fontWeightSemibold }}>
            {render.entity.title}
          </Typography>
          <Typography noWrap sx={monoMetaSx}>
            {metaParts.join(' · ')}
          </Typography>
        </Box>
      </Box>

      {visibleBlocks.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {visibleBlocks.map((block, i) => (
            <BlockRow key={`${block.name}-${i}`} block={block} />
          ))}
          {hiddenCount > 0 && (
            <Typography
              sx={{
                ...monoMetaSx,
                fontSize: '9px',
                px: spacing.md,
                py: '7px',
                borderTop: '1px solid rgba(248, 246, 244, 0.09)',
              }}
            >
              +{hiddenCount} more block{hiddenCount === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>
      )}
    </CardShell>
  )
}
